<template>
    <div>
        <div class="title d-flex justify-content-between align-items-center">
            <div class="text mb-0">Cubex Settings</div>
            <div v-if="enableCubex && hasPermission">
                <button
                    class="btn btn-light mr-2"
                    @click="onCancel"
                    :disabled="!isChanged || isLoading"
                >
                    Cancel
                </button>
                <button
                    class="btn btn-dark"
                    @click="onSubmit"
                    :disabled="!isChanged || !isFormValid || isLoading"
                >
                    <span>Submit</span>
                </button>
            </div>
        </div>

        <loader-list v-if="isLoading || !hasPermission" />
        <div v-else>
            <div class="row mb-4">
                <div class="col-lg-8">
                    <div class="d-flex">
                        <toggle-input
                            id="enableCubex"
                            v-model="enableCubex"
                            @change="onToggle"
                        />
                        <p>
                            This add-on enables integration with Cubex to
                            streamline your clinic's inventory management and
                            medication dispensing directly through Daysmart Vet.
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="enableCubex && hasPermission">
                <div class="row">
                    <div class="col-lg-8 mb-3">
                        <div class="row d-md-flex align-items-center">
                            <label class="mb-3 mb-md-0 col-md-3"
                                >Database Name</label
                            >
                            <div class="col-md-9 px-0">
                                <input
                                    type="text"
                                    name="dbName"
                                    class="form-control"
                                    v-model="formData.dbName"
                                    v-validate="'required'"
                                    :class="{
                                        'border-danger': errors.has('dbName')
                                    }"
                                    placeholder="Database name (required)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-8 mb-3">
                        <div class="row d-md-flex align-items-center">
                            <label class="mb-3 mb-md-0 col-md-3"
                                >Database User</label
                            >
                            <div class="col-md-9 px-0">
                                <input
                                    type="text"
                                    name="dbUser"
                                    class="form-control"
                                    v-model="formData.dbUser"
                                    v-validate="'required'"
                                    :class="{
                                        'border-danger': errors.has('dbUser')
                                    }"
                                    placeholder="Database user (required)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-8 mb-3">
                        <div class="row d-md-flex align-items-center">
                            <label class="mb-3 mb-md-0 col-md-3"
                                >Database Password</label
                            >
                            <div class="col-md-9 px-0">
                                <input
                                    type="password"
                                    name="dbPassword"
                                    class="form-control"
                                    v-model="formData.dbPassword"
                                    v-validate="'required'"
                                    :class="{
                                        'border-danger': errors.has(
                                            'dbPassword'
                                        )
                                    }"
                                    placeholder="Database password (required)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Confirmation Modal -->
        <modal-dialog
            ref="confirmModal"
            confirm-btn-txt="Disable"
            :is-loading="isLoading"
        >
            <template #title>
                Disable Cubex
            </template>
            <template #body>
                <p>
                    Are you sure you want to disable Cubex integration? This
                    action will remove all Cubex settings.
                </p>
            </template>
            <template #confirmBtn>
                <button
                    type="button"
                    class="btn btn-danger"
                    @click="confirmDisable"
                    :disabled="isLoading"
                >
                    Disable
                </button>
            </template>
        </modal-dialog>
    </div>
</template>

<script>
import LoaderList from "@/components/elements/loader-list";
import ModalDialog from "@/components/commons/modal-dialog";
import ToggleInput from "@/components/commons/toggle-input";
import CubexService from "@/services/setting/cubex";
import { isEmpty } from "lodash";

/**
 * CubexSetting Component
 *
 * This component handles the Cubex integration settings for the clinic's inventory management.
 * It allows users to:
 * - Enable/disable Cubex integration
 * - Configure database connection settings
 *
 * The component maintains original and current form data to track changes and handles
 * validation before submission.
 */
export default {
    components: {
        LoaderList,
        ModalDialog,
        ToggleInput
    },
    props: {
        // Existing Cubex settings passed from parent
        cubexSettings: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isLoading: true,
            enableCubex: false,
            isChanged: false,
            // Form data for Cubex configuration
            formData: {
                dbName: "",
                dbUser: "",
                dbPassword: "",
            },
            // Keeps track of original data to detect changes
            originalData: {
                dbName: "",
                dbUser: "",
                dbPassword: "",
            }
        };
    },
    watch: {
        // Watch for changes in form data to enable/disable submit button
        formData: {
            deep: true,
            handler(value) {
                this.isChanged = Object.keys(this.formData).some(
                    key => this.formData[key] !== this.originalData[key]
                );
            }
        },
        // Initialize component when settings are provided
        cubexSettings: {
            immediate: true,
            handler(settings) {
                if (settings != null) {
                    this.isLoading = false;
                }

                if (!isEmpty(settings)) {
                    this.formData = {
                        dbName: settings.database_name,
                        dbUser: settings.database_user,
                        dbPassword: settings.database_password,
                    };
                    this.originalData = { ...this.formData };
                    this.enableCubex = true;
                }
            }
        }
    },
    computed: {
        isFormValid() {
            return (
                this.formData.dbName &&
                this.formData.dbUser &&
                this.formData.dbPassword
            );
        },
        hasPermission() {
            return this._hasAccess("setting.extension.update");
        }
    },
    methods: {
        /**
         * Handles the enable/disable toggle for Cubex integration
         * Shows confirmation modal when trying to disable
         */
        onToggle(newValue) {
            if (!newValue && !isEmpty(this.cubexSettings)) {
                this.$refs.confirmModal.open();

                this.$nextTick(() => {
                    this.enableCubex = true;
                });
            }
        },

        /**
         * Confirms the disable action after modal confirmation
         */
        async confirmDisable() {
            this.enableCubex = false;
            await this.disableCubex();
            this.$refs.confirmModal.close();
        },

        /**
         * Disables Cubex integration through API call
         */
        async disableCubex() {
            try {
                this.isLoading = true;
                const response = await CubexService.disableCubex({
                    businessId: this._setting.business.id
                });

                if (response?.message?.code === 200) {
                    this.enableCubex = false;
                    this._message("Cubex disabled successfully");
                    this.resetOriginalData();
                    this.resetFormData();
                    this.$emit("reload");
                } else {
                    this.showError(response?.message?.description);
                }

                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this._message("Failed to disable Cubex", "danger");
            }
        },

        /**
         * Handles form submission
         * - Validates all required fields
         * - Determines if this is a new configuration or update
         * - Makes appropriate API calls based on state
         */
        async onSubmit() {
            let valid = await this.$validator.validateAll();

            if (!valid) {
                this.showError("Please fill in all required fields");
                return false;
            }

            try {
                this.isLoading = true;

                const isOriginalDataEmpty = Object.values(
                    this.originalData
                ).every(value => !value);

                if (isOriginalDataEmpty) {
                    // Handle new configuration
                    const response = await CubexService.enableCubex({
                        businessId: this._setting.business.id,
                        databaseName: this.formData.dbName,
                        databaseUser: this.formData.dbUser,
                        databasePassword: this.formData.dbPassword,
                    });

                    if (response?.message?.code === 200) {
                        this.originalData = { ...this.formData };
                        this._message("Cubex enabled successfully");
                        this.$emit("reload");
                    } else {
                        this.showError(response?.message?.description);
                    }
                } else if (
                    JSON.stringify(this.formData) !==
                    JSON.stringify(this.originalData)
                ) {
                    // Handle settings update
                    const response = await CubexService.updateCubexSetting({
                        businessId: this._setting.business.id,
                        databaseName: this.formData.dbName,
                        databaseUser: this.formData.dbUser,
                        databasePassword: this.formData.dbPassword,
                    });

                    if (response?.message?.code === 200) {
                        this.originalData = { ...this.formData };
                        this._message("Cubex settings updated successfully");
                        this.$emit("reload");
                    } else {
                        this.showError(response?.message?.description);
                    }
                }

                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this._message(error.message, "danger");
            }
        },

        /**
         * Resets form data to original values
         */
        onCancel() {
            this.formData = { ...this.originalData };
        },

        /**
         * Displays error message with custom or default text
         */
        showError(message) {
            let errorMessage = message
                ? message
                : "Failed to update Cubex settings";
            this._message(errorMessage, "danger");
        },

        /**
         * Resets original data to empty values
         */
        resetOriginalData() {
            this.originalData = {
                dbName: "",
                dbUser: "",
                dbPassword: "",
            };
        },

        /**
         * Resets form data to empty values
         */
        resetFormData() {
            this.formData = {
                dbName: "",
                dbUser: "",
                dbPassword: "",
            };
        }
    }
};
</script>

<style scoped>
.switch-toggle {
    display: flex;
    align-items: center;
}

.switch-toggle::before {
    position: relative;
    pointer-events: all !important;
}

.switch-label {
    pointer-events: none;
    margin-left: 10px;
}

.custom-switch-input-only > p {
    margin-top: -21px;
}
</style>
